// File: app/src/hooks/useColdWarmup.js
//
// Longer warm-up in the cold (issue #76). The Khronos Timer only reports its
// temperature while a session runs, so the check happens here, during the
// warm-up, not when the session is generated. The first reading of the session
// is kept: the sensor sits inside the timer's case and drifts up as it runs.
// One offer per session; Add or Not today settles it.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { subscribeDeviceState } from "../services/settingsChannel";
import { proposeColdWarmup } from "../utils/coldWarmup";

/**
 * @param {object} args
 * @param {object} args.workoutPlan
 * @param {string} args.status          engine status (useWorkoutCoach)
 * @param {Function} args.getTrimContext  engine position (useWorkoutCoach)
 * @param {Function} args.applySessionEdit
 * @param {Function} [args.showToast]
 */
export const useColdWarmupOffer = ({
  workoutPlan,
  status,
  getTrimContext,
  applySessionEdit,
  showToast,
}) => {
  const inSession = status !== "ready" && status !== "finished";
  const [tempC, setTempC] = useState(null);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!inSession) {
      setTempC(null);
      setSettled(false);
      return undefined;
    }
    return subscribeDeviceState((evt) => {
      const t = evt?.env?.temperature_c;
      if (typeof t === "number" && Number.isFinite(t)) {
        setTempC((prev) => prev ?? t);
      }
    });
  }, [inSession]);

  const ctx = inSession && !settled && tempC != null ? getTrimContext?.() : null;
  const currentIndex = ctx?.currentIndex ?? null;
  const proposal = useMemo(() => {
    if (!ctx) return null;
    return proposeColdWarmup(workoutPlan, { ...ctx, tempC });
    // The engine position only moves when the phase does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutPlan, tempC, currentIndex, settled]);

  const offer = proposal ? { ...proposal, tempC } : null;
  const offerRef = useRef(offer);
  offerRef.current = offer;

  const accept = useCallback(() => {
    const current = offerRef.current;
    if (!current) return;
    applySessionEdit(current.plan);
    setSettled(true);
    showToast?.({ title: "Warm-up extended", description: current.text });
  }, [applySessionEdit, showToast]);

  const decline = useCallback(() => setSettled(true), []);

  return { offer, accept, decline };
};
